/**
 * ============================================================
 * Mining Survey WebGIS
 * Track Module
 * ============================================================
 */

import AppState from "../core/app.js";
import Map from "./map.js";
import UI from "./ui.js";
import CONFIG from "../config/config.js";
import TrackExport from "./export.js";
import Waypoint from "./waypoint.js";

class Track {
    constructor() {
        this.reset();
    }
    calculateDistance(point1, point2) {
        const R = 6371000; // meter

        const toRad = (deg) => (deg * Math.PI) / 180;

        const lat1 = toRad(point1.latitude);
        const lat2 = toRad(point2.latitude);

        const dLat = toRad(point2.latitude - point1.latitude);
        const dLon = toRad(point2.longitude - point1.longitude);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c =
            2 *
            Math.atan2(
                Math.sqrt(a),

                Math.sqrt(1 - a),
            );

        return R * c;
    }

    validateAccuracy(position) {
        if (position.accuracy == null) {
            return true;
        }

        return position.accuracy <= CONFIG.TRACK.MAX_ACCURACY;
    }

    validateDuplicate(lastPoint, position) {
        if (!lastPoint) return true;

        const sameLat = Math.abs(lastPoint.latitude - position.latitude) < 0.000001;

        const sameLon = Math.abs(lastPoint.longitude - position.longitude) < 0.000001;

        return !(sameLat && sameLon);
    }

    validateMinimumDistance(lastPoint, position) {
        if (!lastPoint) return true;

        const distance = this.calculateDistance(lastPoint, position);

        return distance >= CONFIG.TRACK.MIN_DISTANCE;
    }

    validateJump(lastPoint, position) {
        if (!lastPoint) return true;

        const distance = this.calculateDistance(lastPoint, position);

        return distance <= CONFIG.TRACK.MAX_SEGMENT;
    }

    validateSpeed(lastPoint, position) {
        if (!lastPoint) {
            return true;
        }

        const distance = this.calculateDistance(lastPoint, position);

        const deltaTime = (position.timestamp - lastPoint.timestamp) / 1000;

        if (deltaTime <= 0) {
            return false;
        }

        const speed = (distance / deltaTime) * 3.6;

        console.log(
            "Segment Speed",

            speed.toFixed(2),

            "km/h",
        );
        return speed <= CONFIG.TRACK.MAX_WALK_SPEED;
    }

    reset() {
        Map.clearTrack();

        AppState.track.isRecording = false;

        AppState.track.isPaused = false;

        AppState.track.startTime = null;

        AppState.track.endTime = null;

        AppState.track.distance = 0;

        AppState.track.elapsed = 0;

        AppState.track.currentSpeed = 0;

        AppState.track.averageSpeed = 0;

        AppState.track.lastSegment = 0;

        AppState.track.points = [];

        // Disable Save
        UI.enableSaveButton(false);

        AppState.track.acceptedPoints = 0;

        AppState.track.ignoredPoints = 0;
    }

    getState() {
        if (!AppState.track.isRecording) {
            return "idle";
        }

        if (AppState.track.isPaused) {
            return "paused";
        }

        return "recording";
    }

    start() {
        this.reset();
        Waypoint.reset();

        AppState.track.isRecording = true;
        AppState.track.isPaused = false;

        AppState.track.startTime = new Date();

        AppState.track.distance = 0;

        AppState.track.elapsed = 0;

        AppState.track.currentSpeed = 0;

        AppState.track.averageSpeed = 0;

        AppState.track.lastSegment = 0;

        UI.updateTrackStatistics(this.getStatistics());

        // Disable Save saat recording dimulai
        UI.enableSaveButton(false);

        console.log("▶ Track Recording Started");
    }

    pause() {
        if (!AppState.track.isRecording) return;

        if (AppState.track.isPaused) return;

        AppState.track.isPaused = true;

        UI.updateTrackStatistics(this.getStatistics());

        console.log("⏸ Track Paused");
    }

    resume() {
        if (!AppState.track.isRecording) return;

        if (!AppState.track.isPaused) return;

        AppState.track.isPaused = false;

        UI.updateTrackStatistics(this.getStatistics());

        console.log("▶ Track Resumed");
    }

    stop() {
        if (!AppState.track.isRecording) return;

        AppState.track.isRecording = false;
        AppState.track.isPaused = false;

        AppState.track.endTime = new Date();

        AppState.track.elapsed = Date.now() - AppState.track.startTime;

        UI.updateTrackStatistics(this.getStatistics());

        if (AppState.track.points.length > 1) {
            UI.enableSaveButton(true);
        }

        console.log("■ Track Recording Stopped");

        console.log(AppState.track.points.length, "points recorded");

        console.table({
            Distance: AppState.track.distance.toFixed(2) + " m",

            Duration: this.formatDuration(AppState.track.elapsed),

            Average: AppState.track.averageSpeed.toFixed(2) + " km/h",

            Points: AppState.track.points.length,
        });
    }

    updateStatistics(position) {
        if (!AppState.track.startTime) return;

        AppState.track.elapsed = Date.now() - AppState.track.startTime;

        AppState.track.currentSpeed = (position.speed ?? 0) * 3.6;

        if (AppState.track.elapsed > 0) {
            const hours = AppState.track.elapsed / 3600000;

            if (hours > 0) {
                AppState.track.averageSpeed = AppState.track.distance / 1000 / hours;
            }
        }
    }

    formatDuration(milliseconds) {
        const totalSeconds = Math.floor(milliseconds / 1000);

        const hours = Math.floor(totalSeconds / 3600);

        const minutes = Math.floor((totalSeconds % 3600) / 60);

        const seconds = totalSeconds % 60;

        return [String(hours).padStart(2, "0"), String(minutes).padStart(2, "0"), String(seconds).padStart(2, "0")].join(":");
    }

    getStatistics() {
        return {
            distance: AppState.track.distance,

            elapsed: AppState.track.elapsed,

            duration: this.formatDuration(AppState.track.elapsed),

            currentSpeed: AppState.track.currentSpeed,

            averageSpeed: AppState.track.averageSpeed,

            lastSegment: AppState.track.lastSegment,

            points: AppState.track.points.length,

            acceptedPoints: AppState.track.acceptedPoints,

            ignoredPoints: AppState.track.ignoredPoints,
        };
    }

    addPoint(position) {
        if (!AppState.track.isRecording) return;

        if (AppState.track.isPaused) return;

        const lastPoint = AppState.track.points.at(-1);

        // ==========================
        // Accuracy Filter
        // ==========================

        if (!this.validateAccuracy(position)) {
            AppState.track.ignoredPoints++;

            return;
        }

        // ==========================
        // Duplicate Filter
        // ==========================

        if (!this.validateDuplicate(lastPoint, position)) {
            AppState.track.ignoredPoints++;

            return;
        }

        // ==========================
        // Minimum Distance Filter
        // ==========================

        if (!this.validateMinimumDistance(lastPoint, position)) {
            AppState.track.ignoredPoints++;

            return;
        }

        // ==========================
        // GPS Jump Filter
        // ==========================

        if (!this.validateJump(lastPoint, position)) {
            AppState.track.ignoredPoints++;

            return;
        }

        // ==========================
        // Speed Filter
        // ==========================

        if (!this.validateSpeed(lastPoint, position)) {
            AppState.track.ignoredPoints++;

            return;
        }

        // ==========================
        // Save Point
        // ==========================

        AppState.track.points.push({
            latitude: position.latitude,

            longitude: position.longitude,

            altitude: position.altitude,

            accuracy: position.accuracy,

            speed: position.speed,

            heading: position.heading,

            timestamp: position.timestamp,
        });

        AppState.track.acceptedPoints++;

        // ==========================
        // Distance
        // ==========================

        if (lastPoint) {
            const segment = this.calculateDistance(lastPoint, position);

            AppState.track.lastSegment = segment;

            AppState.track.distance += segment;
        } else {
            AppState.track.lastSegment = 0;
        }

        // ==========================
        // Statistics
        // ==========================

        this.updateStatistics(position);

        // ==========================
        // Draw Track
        // ==========================

        Map.renderTrack(AppState.track.points);

        // ==========================
        // Update UI
        // ==========================

        UI.updateTrackStatistics(this.getStatistics());
    }

    getPointCount() {
        return AppState.track.points.length;
    }

    askTrackName() {
        const name = prompt("Track Name", "Survey");

        if (!name) {
            return null;
        }

        return name.trim();
    }

    save() {
        if (AppState.track.points.length < 2 && AppState.waypoints.length === 0) {
            alert("Belum ada Track atau Waypoint untuk disimpan.");
            return;
        }
        const now = new Date();

        const timestamp =
            now.getFullYear() +
            String(now.getMonth() + 1).padStart(2, "0") +
            String(now.getDate()).padStart(2, "0") +
            "_" +
            String(now.getHours()).padStart(2, "0") +
            String(now.getMinutes()).padStart(2, "0") +
            String(now.getSeconds()).padStart(2, "0");

        const trackName = this.askTrackName();

        if (!trackName) {
            return;
        }

        // Hindari karakter yang tidak valid pada nama file
        const safeTrackName = trackName.trim().replace(/[\\/:*?"<>|]/g, "_");

        const filename = `${safeTrackName}_${timestamp}.geojson`;

        TrackExport.downloadGeoJSON(
            {
                name: trackName,

                points: AppState.track.points,

                waypoints: AppState.waypoints,

                distance: AppState.track.distance,

                duration: this.formatDuration(AppState.track.elapsed),

                averageSpeed: AppState.track.averageSpeed,

                startTime: AppState.track.startTime,

                endTime: AppState.track.endTime,
            },

            filename,
        );
    }
}

export default new Track();
